import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";

/** Short message at the bottom of the screen. */
export function useToast(duration = 2600) {
  const [message, setMessage] = useState("");
  const timer = useRef(null);
  const show = useCallback(
    (text) => {
      clearTimeout(timer.current);
      setMessage(text);
      timer.current = setTimeout(() => setMessage(""), duration);
    },
    [duration],
  );
  useEffect(() => () => clearTimeout(timer.current), []);
  const toast = message ? (
    <div className="ep-toast" role="status" aria-live="polite">
      {message}
    </div>
  ) : null;
  return { show, toast };
}

/**
 * Favourites kept in this browser only: the platform has no wishlist API
 * yet. Shared across screens; a storage failure just means no favourites.
 */
const KEY = "shopFavorites";
let favorites = (() => {
  try {
    return new Set(JSON.parse(localStorage.getItem(KEY) ?? "[]"));
  } catch {
    return new Set();
  }
})();
const listeners = new Set();

export function useFavorites() {
  const snapshot = useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => favorites,
  );
  const toggle = useCallback((productId) => {
    const next = new Set(favorites);
    if (next.has(productId)) next.delete(productId);
    else next.add(productId);
    favorites = next;
    try {
      localStorage.setItem(KEY, JSON.stringify([...next]));
    } catch {
      // Not saved; still toggled for this visit.
    }
    listeners.forEach((listener) => listener());
    return next.has(productId);
  }, []);
  return { isFavorite: (id) => snapshot.has(id), toggle };
}
